/**
 * Communications page.
 *
 * Two tabs:
 *  • Message log — chronological list of MessageLog rows with patient/lead context. Every send
 *                  path (appointment events, automations, manual sends) writes here, so this is
 *                  the ledger of "what we sent to whom".
 *  • Templates   — the SINGLE place WhatsApp Cloud templates are created, edited, submitted to
 *                  Meta, synced, enabled/disabled or deleted. Automations (Campaigns page) only
 *                  ever READ this list to pick a template — they don't manage it. Below the
 *                  WhatsApp templates: the separate DB-only MessageTemplate registry used for
 *                  SMS/Email and free-text automation copy.
 */
import React, { useState } from 'react';
import {
  MessageSquare, FileText, Send, Edit3, Trash2, Plus, X, Zap, Wrench,
  RefreshCw, Search, AlertTriangle, ShieldCheck, Link2Off, Power, LayoutGrid,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useListTemplatesQuery, useListMessagesQuery,
  useCreateTemplateMutation, useUpdateTemplateMutation,
  useGetWhatsAppStatusQuery,
} from '../../api/communicationsApi';
import {
  useListWhatsAppTemplatesQuery,
  useGetWhatsAppTemplateConfigQuery,
  useSyncWhatsAppTemplatesMutation,
  useDeleteWhatsAppTemplateMutation,
  useUpdateTemplateBindingsMutation,
} from '../../api/campaignsApi';
import TemplateEditorModal from './TemplateEditorModal';
import SendMessageModal from './SendMessageModal';
import MediaGalleryModal from '../campaigns/MediaGalleryModal';

const CHANNEL_COLOR = { Sms: 'b-blue', WhatsApp: 'b-green', Email: 'b-purple' };
const STATUS_COLOR = { Pending: 'b-gray', Sent: 'b-blue', Delivered: 'b-teal', Opened: 'b-green', Replied: 'b-green', Failed: 'b-red' };

const WA_STATUS_COLOR = {
  Draft: 'b-gray', Pending: 'b-amber', Approved: 'b-green', Rejected: 'b-red',
  Paused: 'b-amber', Disabled: 'b-gray', PendingDeletion: 'b-gray', InAppeal: 'b-blue',
};
const WA_STATUS_HINT = {
  Draft: 'Created here but not submitted to Meta yet.',
  Pending: 'Submitted — waiting on Meta review.',
  Approved: 'Approved by Meta and usable for sending.',
  Rejected: 'Rejected by Meta. Edit and resubmit.',
  Paused: 'Paused by Meta for low quality. Recovers on its own.',
  Disabled: 'Disabled at Meta, or deleted remotely.',
  PendingDeletion: 'Deletion requested at Meta.',
  InAppeal: 'Rejection is being appealed.',
};
const WA_QUALITY_COLOR = { GREEN: 'b-green', YELLOW: 'b-amber', RED: 'b-red' };

/** Templates fired directly by appointment events — their values come from the booking, not a
 * CRM-field link, so the "unlinked variable" warning doesn't apply to them. */
const EVENT_TEMPLATES = {
  confirmation: 'Sent automatically when a patient books their very first appointment.',
  appointment_confirm: 'Sent automatically when a returning patient books an appointment.',
  appointment_reminder: 'Sent automatically at 09:00 IST to everyone with an appointment that day.',
};

export default function CommunicationsPage() {
  const [tab, setTab] = useState('messages');
  // Management-only entry point into the gallery — no lockKind (browse everything) and no
  // onSelect (there's no field here to fill in; staff are just curating the library).
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  // Failed sends are hidden from the default view (staff only want to see what actually went
  // out) — picking "Failed" from the status dropdown still shows them for debugging.
  const [filter, setFilter] = useState({ page: 1, pageSize: 25, channel: '', status: '', hideFailed: true });
  const { data: messages, isFetching: mLoading } = useListMessagesQuery(filter, { skip: tab !== 'messages' });
  const { data: templates, isFetching: tLoading } = useListTemplatesQuery(undefined, { skip: tab !== 'templates' });
  const { data: waStatus } = useGetWhatsAppStatusQuery();
  const [showSend, setShowSend] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null); // MessageTemplate object, or 'new'

  // WhatsApp (Meta) templates — full management lives here. Kept as its own component so its
  // filter/search state doesn't leak into the rest of the page.
  const [waFilter, setWaFilter] = useState({ search: '', status: '' });
  const { data: waConfig } = useGetWhatsAppTemplateConfigQuery();
  const { data: waTemplates, isFetching: waLoading } = useListWhatsAppTemplatesQuery(waFilter, { skip: tab !== 'templates' });
  const [syncWa, { isLoading: waSyncing }] = useSyncWhatsAppTemplatesMutation();
  const [removeWa] = useDeleteWhatsAppTemplateMutation();
  const [toggleWaActive] = useUpdateTemplateBindingsMutation();
  const [editWaTemplate, setEditWaTemplate] = useState(null); // WhatsAppTemplate object, or 'new'

  const runWaSync = async () => {
    try {
      const r = await syncWa().unwrap();
      if (r.error) { toast.error(r.error); return; }
      // Report the real numbers — "synced" alone hides the difference between "nothing changed"
      // and "nothing came back".
      toast.success(
        `Synced ${r.fetched} template${r.fetched === 1 ? '' : 's'} — ` +
        `${r.created} new, ${r.updated} updated, ${r.disabled} disabled.`
      );
    } catch (e) {
      toast.error(e?.data?.error?.message || 'Sync failed.');
    }
  };

  const removeWaTemplate = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? This also deletes it from Meta, and the name can't be reused for about 30 days.`)) return;
    try {
      await removeWa(t.id).unwrap();
      toast.success(`Deleted ${t.name}.`);
    } catch (e) {
      toast.error(e?.data?.error?.message || 'Could not delete the template.');
    }
  };

  // Quick on/off from the card, without opening the editor. Synced templates start disabled by
  // design — this is the one-click way to arm one once its variables are linked.
  const toggleWaEnabled = async (t) => {
    try {
      await toggleWaActive({ id: t.id, isActive: !t.isActive, variables: [] }).unwrap();
      toast.success(t.isActive ? `${t.name} disabled.` : `${t.name} enabled.`);
    } catch (e) {
      toast.error(e?.data?.error?.message || 'Could not change the template.');
    }
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb"><span>Home</span><span className="bc-sep">/</span><span className="bc-cur">Communications</span></div>
        <div className="page-header-row">
          <div>
            <h1>Communications</h1>
            <div className="page-header-sub">WhatsApp / SMS / Email log and template registry.</div>
          </div>
          <button className="btn btn-primary" onClick={() => setShowSend(true)}><Send size={16} /> Send Message</button>
        </div>
      </div>

      {waStatus?.paused && (
        <div className="maintenance-banner" role="status">
          <Wrench size={16} />
          <span><strong>WhatsApp — Under Maintenance.</strong> Outgoing WhatsApp messages (auto confirmations, reminders and manual sends) are temporarily paused. SMS and Email are unaffected.</span>
        </div>
      )}

      <div className="tabs">
        <button className={`tab-btn ${tab === 'messages' ? 'active' : ''}`} onClick={() => setTab('messages')}>
          <MessageSquare size={14} /> Message log
        </button>
        <button className={`tab-btn ${tab === 'templates' ? 'active' : ''}`} onClick={() => setTab('templates')}>
          <FileText size={14} /> Templates
        </button>
      </div>

      {tab === 'messages' && (
        <>
          <div className="toolbar">
            <select className="form-control" style={{ width: 180 }} value={filter.channel}
              onChange={(e) => setFilter({ ...filter, channel: e.target.value, page: 1 })}>
              <option value="">All channels</option>
              {Object.keys(CHANNEL_COLOR).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="form-control" style={{ width: 180 }} value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value, page: 1 })}>
              <option value="">All statuses</option>
              {Object.keys(STATUS_COLOR).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Patient / Recipient</th>
                  <th>Channel</th>
                  <th>Template / Body</th>
                  <th>Status</th>
                  <th>Sent</th>
                </tr>
              </thead>
              <tbody>
                {mLoading && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24 }}>
                    <div className="skeleton" style={{ height: 20, maxWidth: 280, margin: '0 auto' }} />
                  </td></tr>
                )}
                {(messages?.items ?? []).map((m) => <MessageRow key={m.id} m={m} />)}
                {!mLoading && messages?.items?.length === 0 && (
                  <tr><td colSpan={5}><div className="empty-state"><h3>No messages yet</h3><p>Once you book an appointment, the auto-sent WhatsApp confirmation will show up here.</p></div></td></tr>
                )}
              </tbody>
            </table>
          </div>

          {messages?.totalCount > filter.pageSize && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
              <div className="td-muted" style={{ fontSize: '0.8rem' }}>
                Showing {(filter.page - 1) * filter.pageSize + 1}–{Math.min(filter.page * filter.pageSize, messages.totalCount)} of {messages.totalCount}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-outline btn-sm" disabled={filter.page <= 1}
                  onClick={() => setFilter({ ...filter, page: filter.page - 1 })}>Prev</button>
                <button className="btn btn-outline btn-sm" disabled={filter.page * filter.pageSize >= (messages?.totalCount ?? 0)}
                  onClick={() => setFilter({ ...filter, page: filter.page + 1 })}>Next</button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'templates' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 0 10px', gap: 8, flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-secondary)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              WhatsApp templates
            </h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline btn-sm" onClick={() => setShowMediaGallery(true)}>
                <LayoutGrid size={14} /> Media Gallery
              </button>
              <button className="btn btn-outline btn-sm" onClick={runWaSync} disabled={waSyncing || !waConfig?.configured}>
                <RefreshCw size={14} /> {waSyncing ? 'Syncing…' : 'Sync from Meta'}
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => setEditWaTemplate('new')}>
                <Plus size={14} /> New template
              </button>
            </div>
          </div>

          {waConfig && !waConfig.configured && (
            <div className="maintenance-banner" role="status">
              <AlertTriangle size={16} />
              <span>
                <strong>Template management isn’t configured.</strong> Set <code>MetaCloud:WabaId</code> in
                appsettings and the <code>WA_MANAGEMENT_TOKEN</code> environment variable to a token with the
                <code> whatsapp_business_management</code> permission, then restart the API.
              </span>
            </div>
          )}

          <div className="toolbar">
            <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 300 }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input className="form-control" style={{ paddingLeft: 28 }} placeholder="Search name or body…"
                value={waFilter.search} onChange={(e) => setWaFilter({ ...waFilter, search: e.target.value })} />
            </div>
            <select className="form-control" style={{ width: 170 }} value={waFilter.status}
              onChange={(e) => setWaFilter({ ...waFilter, status: e.target.value })}>
              <option value="">All statuses</option>
              {Object.keys(WA_STATUS_COLOR).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {waConfig?.lastSyncedAt && (
              <div className="td-muted" style={{ fontSize: '0.76rem', marginLeft: 'auto' }}>
                Last synced {new Date(waConfig.lastSyncedAt).toLocaleString()}
              </div>
            )}
          </div>

          {/* alignItems:start stops the grid stretching every card to match the tallest — the long
              confirmation template was leaving huge dead space in every other card. */}
          <div className="g-auto" style={{ alignItems: 'start' }}>
            {waLoading && <div className="skeleton" style={{ height: 140 }} />}
            {(waTemplates ?? []).map((t) => (
              <WaTemplateCard key={t.id} t={t} paused={waStatus?.paused} pausedReason={waStatus?.reason}
                onEdit={() => setEditWaTemplate(t)} onDelete={() => removeWaTemplate(t)}
                onToggle={() => toggleWaEnabled(t)} />
            ))}
            {!waLoading && (waTemplates ?? []).length === 0 && (
              <div className="card card-body" style={{ gridColumn: '1 / -1' }}>
                <div className="empty-state">
                  <h3>No WhatsApp templates</h3>
                  <p>
                    {waConfig?.configured
                      ? 'Nothing on this WhatsApp Business Account yet. Create one here, or hit “Sync from Meta” to pull in anything that already exists there.'
                      : 'Configure template management above to pull templates from your WhatsApp Business Account.'}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '20px 0 10px' }}>
            <h3 style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-secondary)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Message templates
            </h3>
            <button className="btn btn-outline btn-sm" onClick={() => setEditTemplate('new')}><Plus size={14} /> New template</button>
          </div>
          <div className="g-auto" style={{ alignItems: 'start' }}>
            {tLoading && <div className="skeleton" style={{ height: 100 }} />}
            {(templates ?? []).map((t) => (
              <div key={t.id} className="card" style={{ minWidth: 0 }}>
                <div className="card-header" style={{ display: 'block' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <h3 style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{t.name}</h3>
                    <button className="btn-icon-sm" style={{ flexShrink: 0 }} title="Edit template"
                      onClick={() => setEditTemplate(t)}><Edit3 size={14} /></button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                    <span className={`badge ${CHANNEL_COLOR[t.channel] || 'b-gray'}`}>{t.channel}</span>
                    <span className="badge b-gray">{t.category}</span>
                    {t.key && (
                      <span className="badge b-blue" title={`Fired automatically by: ${t.triggerEvent || t.key}`}>
                        <Zap size={10} /> {t.key}
                      </span>
                    )}
                  </div>
                </div>
                <div className="card-body" style={{ minWidth: 0 }}>
                  {t.subject && <div className="td-bold" style={{ marginBottom: 6, overflowWrap: 'anywhere' }}>{t.subject}</div>}
                  {t.body
                    ? <div className="td-muted" style={{ whiteSpace: 'pre-wrap', fontSize: '0.83rem', overflowWrap: 'anywhere', maxHeight: 170, overflowY: 'auto' }}>{t.body}</div>
                    : <div className="td-muted" style={{ fontSize: '0.83rem', fontStyle: 'italic' }}>Body not written yet — automation records the trigger but sends nothing until the copy is added.</div>}
                </div>
              </div>
            ))}
            {!tLoading && (templates ?? []).length === 0 && (
              <div className="card card-body" style={{ gridColumn: '1 / -1' }}>
                <div className="empty-state"><h3>No manual templates</h3><p>Create message templates for ad-hoc reminders, follow-ups and broadcasts.</p></div>
              </div>
            )}
          </div>
        </>
      )}

      {showSend && <SendMessageModal onClose={() => setShowSend(false)} />}
      {editTemplate && (
        <TemplateEditModal
          template={editTemplate === 'new' ? null : editTemplate}
          onClose={() => setEditTemplate(null)}
        />
      )}
      {editWaTemplate && (
        <TemplateEditorModal
          template={editWaTemplate === 'new' ? null : editWaTemplate}
          onClose={() => setEditWaTemplate(null)}
        />
      )}
      {showMediaGallery && <MediaGalleryModal onClose={() => setShowMediaGallery(false)} />}
    </>
  );
}

function WaTemplateCard({ t, paused, pausedReason, onEdit, onDelete, onToggle }) {
  const unbound = (t.variables ?? []).filter((v) => !v.bindingToken);
  const eventNote = EVENT_TEMPLATES[t.name];

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <div className="card-header" style={{ display: 'block' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <h3 className="td-mono" style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{t.name}</h3>
          <span style={{ display: 'inline-flex', gap: 2, flexShrink: 0 }}>
            <button className="btn-icon-sm" title={t.isActive ? 'Disable this template' : 'Enable this template'}
              onClick={onToggle}>
              <Power size={14} color={t.isActive ? 'var(--green-700)' : undefined} />
            </button>
            <button className="btn-icon-sm" title="Edit template" onClick={onEdit}><Edit3 size={14} /></button>
            <button className="btn-icon-sm" title="Delete template" onClick={onDelete}><Trash2 size={14} /></button>
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
          {paused
            ? <span className="badge b-amber" title={pausedReason}><Wrench size={10} /> Under maintenance</span>
            : <span className={`badge ${WA_STATUS_COLOR[t.status] || 'b-gray'}`} title={WA_STATUS_HINT[t.status]}>{t.status}</span>}
          <span className="badge b-purple">{t.category}</span>
          <span className="badge b-gray">{t.languageCode}</span>
          {/* Media templates behave differently — every send carries a file — so they're worth
              spotting at a glance when picking one for a campaign. */}
          {['Image', 'Video', 'Document'].includes(t.headerFormat) && (
            <span className="badge b-blue" title={`Every send carries a ${t.headerFormat.toLowerCase()}`}>
              {t.headerFormat === 'Video' ? '🎬' : t.headerFormat === 'Document' ? '📄' : '🖼'} {t.headerFormat} header
            </span>
          )}
          {t.qualityScore && (
            <span className={`badge ${WA_QUALITY_COLOR[t.qualityScore] || 'b-gray'}`} title="Meta quality rating">
              {t.qualityScore}
            </span>
          )}
          {t.isActive
            ? <span className="badge b-blue" title="Enabled for use in the CRM"><ShieldCheck size={10} /> Enabled</span>
            : <span className="badge b-gray" title="Not usable in an automation or the inbox until enabled">Not enabled</span>}
        </div>
      </div>

      <div className="card-body" style={{ minWidth: 0 }}>
        {t.rejectedReason && (
          <div style={{ fontSize: '0.8rem', color: 'var(--danger)', marginBottom: 8, display: 'flex', gap: 5 }}>
            <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
            <span style={{ overflowWrap: 'anywhere' }}>{t.rejectedReason}</span>
          </div>
        )}

        {t.headerText && (
          <div className="td-bold" style={{ marginBottom: 6, fontSize: '0.85rem', overflowWrap: 'anywhere' }}>
            {t.headerText}
          </div>
        )}

        <div
          className="td-muted"
          style={{ whiteSpace: 'pre-wrap', fontSize: '0.83rem', overflowWrap: 'anywhere', maxHeight: 170, overflowY: 'auto' }}
        >
          {t.bodyText}
        </div>

        {t.footerText && (
          <div className="td-muted" style={{ fontSize: '0.76rem', marginTop: 6, fontStyle: 'italic', overflowWrap: 'anywhere' }}>
            {t.footerText}
          </div>
        )}

        {(t.variables ?? []).length > 0 && (
          <div style={{ marginTop: 12, display: 'grid', gap: 5 }}>
            <div className="td-bold" style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Variables
            </div>
            {t.variables.map((v) => (
              <div key={v.id} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'baseline', fontSize: '0.78rem' }}>
                <span className="td-mono" style={{ overflowWrap: 'anywhere' }}>{`{{${v.variableName}}}`}</span>
                {v.bindingToken
                  ? <span className="td-mono td-muted" style={{ overflowWrap: 'anywhere' }}>→ {v.bindingToken}</span>
                  : <span className="td-muted" style={{ fontStyle: 'italic' }}>→ not linked</span>}
              </div>
            ))}
          </div>
        )}

        {eventNote ? (
          <div className="td-muted" style={{ fontSize: '0.76rem', marginTop: 10, display: 'flex', gap: 5 }}>
            <ShieldCheck size={12} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{eventNote} Its values are filled in by the booking, so no CRM links are needed.</span>
          </div>
        ) : unbound.length > 0 && (
          <div className="td-muted" style={{ fontSize: '0.76rem', marginTop: 10, display: 'flex', gap: 5 }}>
            <Link2Off size={12} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              {unbound.length} variable{unbound.length === 1 ? '' : 's'} not linked — this template
              can’t be used in an automation yet.
            </span>
          </div>
        )}
        {t.status === 'Approved' && !t.isActive && unbound.length === 0 && (
          <div className="td-muted" style={{ fontSize: '0.76rem', marginTop: 10, display: 'flex', gap: 5 }}>
            <Power size={12} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>Approved and linked, but not enabled — click the power icon above to arm it.</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Create/edit a MessageTemplate — including the automation fields (key + trigger event)
// and the body, which is allowed to stay empty until the copy is designed.
function TemplateEditModal({ template, onClose }) {
  const [form, setForm] = useState(() => ({
    name: template?.name ?? '',
    key: template?.key ?? '',
    triggerEvent: template?.triggerEvent ?? '',
    channel: template?.channel ?? 'WhatsApp',
    subject: template?.subject ?? '',
    body: template?.body ?? '',
    placeholders: template?.placeholders ?? '',
    category: template?.category ?? 'FollowUp',
  }));
  const [createTemplate, { isLoading: creating }] = useCreateTemplateMutation();
  const [updateTemplate, { isLoading: updating }] = useUpdateTemplateMutation();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Template name is required.'); return; }
    try {
      if (template?.id) await updateTemplate({ id: template.id, ...form }).unwrap();
      else await createTemplate(form).unwrap();
      toast.success('Template saved');
      onClose();
    } catch (err) {
      toast.error(err?.data?.error?.message || 'Could not save the template.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <h2>{template ? 'Edit template' : 'New template'}</h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Name *</label>
              <input className="form-control" value={form.name} onChange={set('name')} /></div>
            <div className="form-group"><label className="form-label">Channel</label>
              <select className="form-control" value={form.channel} onChange={set('channel')}>
                <option value="WhatsApp">WhatsApp</option>
                <option value="Sms">SMS</option>
                <option value="Email">Email</option>
              </select></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Key (automation)</label>
              <input className="form-control td-mono" placeholder="e.g. lead_follow_up" value={form.key} onChange={set('key')}
                title="Stable machine key an automated trigger looks this template up by" /></div>
            <div className="form-group"><label className="form-label">Trigger event</label>
              <input className="form-control td-mono" placeholder="e.g. lead_status_follow_up" value={form.triggerEvent} onChange={set('triggerEvent')} /></div>
          </div>
          {form.channel === 'Email' && (
            <div className="form-group"><label className="form-label">Subject</label>
              <input className="form-control" value={form.subject} onChange={set('subject')} /></div>
          )}
          <div className="form-group"><label className="form-label">Body</label>
            <textarea className="form-control" rows={5} value={form.body} onChange={set('body')}
              placeholder="Leave empty until the copy is designed — the automation will log the trigger without sending." /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Placeholders</label>
              <input className="form-control td-mono" placeholder="{{name}}, {{phone}}" value={form.placeholders} onChange={set('placeholders')} /></div>
            <div className="form-group"><label className="form-label">Category</label>
              <select className="form-control" value={form.category} onChange={set('category')}>
                {['Welcome', 'Reminder', 'FollowUp', 'Promotional', 'Receipt', 'Notification', 'Other'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={creating || updating}>
            {(creating || updating) ? 'Saving…' : 'Save template'}
          </button>
        </div>
      </form>
    </div>
  );
}

// One row in the message log. RenderedSubject is the dedup key for auto-sends
// (e.g. "appointment_confirmation:<guid>"); we extract the template name prefix so the
// log shows the friendly template name in its own column.
function MessageRow({ m }) {
  const tpl = parseTemplateName(m.renderedSubject);
  const name = m.recipientPatientName || m.recipientLeadName;
  return (
    <tr>
      <td>
        {name && <div className="td-bold">{name}</div>}
        <div className="td-mono td-muted" style={{ fontSize: '0.78rem' }}>
          {m.recipientPatientCode ? `${m.recipientPatientCode} · ` : ''}{m.recipient}
        </div>
      </td>
      <td><span className={`badge ${CHANNEL_COLOR[m.channel] || 'b-gray'}`}>{m.channel}</span></td>
      <td>
        {tpl && <div className="td-bold" style={{ fontSize: '0.85rem' }}>{tpl}</div>}
        <div className="td-muted" style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
          {m.renderedBody}
        </div>
        {m.status === 'Failed' && m.isPlatformBlocked && (
          // The CRM's send succeeded — WhatsApp/Meta chose not to deliver it afterwards. Called
          // out separately from a genuine CRM failure so staff don't mistake "Meta blocked this"
          // for "the CRM is broken", and know to follow up with the recipient another way.
          <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <ShieldCheck size={13} color="var(--amber-600)" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ color: 'var(--amber-700)', fontSize: '0.78rem', fontWeight: 600 }}>
                Sent by PhysioCore — blocked by WhatsApp
              </div>
              <div className="td-muted" style={{ fontSize: '0.72rem' }}>{m.failureReason}</div>
            </div>
          </div>
        )}
        {m.status === 'Failed' && !m.isPlatformBlocked && m.failureReason && (
          <div style={{ color: 'var(--red-700)', fontSize: '0.76rem', marginTop: 2 }}>{m.failureReason}</div>
        )}
      </td>
      <td>
        {m.status === 'Failed' && m.isPlatformBlocked
          ? <span className="badge b-amber" title="PhysioCore sent this message; WhatsApp declined to deliver it (their anti-spam/quality throttling) — not a CRM issue.">Blocked by WhatsApp</span>
          : <span className={`badge ${STATUS_COLOR[m.status] || 'b-gray'}`}>{m.status}</span>}
      </td>
      <td className="td-muted">{m.sentAt ? new Date(m.sentAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</td>
    </tr>
  );
}

function parseTemplateName(subject) {
  if (!subject) return null;
  // Auto-send subjects are "<templateName>:<id>[:<day>]". Manual SendMessageModal sends use a
  // human-readable subject (no colon prefix), so anything without a leading word followed by
  // a colon is treated as a real subject and displayed as-is.
  const m = subject.match(/^([a-z][a-z0-9_]*):.+/i);
  return m ? m[1] : subject;
}
